import saleAPI from "services/sale";
import { useContext, useEffect, useRef, useState } from "react";
import { usePromise } from "services/index";
import { ErrorAndLoadingContext } from "context/ErrorHandlingAndLoading";
import { getTranslation, TransformToNumber, useHandlerParamsApi } from "utils";
import { setBasket, useGlobalStateDispatch, useGlobalState, setOldBasket } from "context/GlobalStateContext";
import { useSettingsApps } from "context/SettingsAppsContext"; 
import errorTranslation from "template/global/translation/errorTranslation";

/**
* Custom hook to create a sale document.
*
* This hook manages the state of the created sale document and provides
* a function to initiate the creation request.
*
* @returns {Object} - An object containing the following properties: 
* @returns {Function} createSaleDocument - Function to initiate the request to create a sale document.
*/
export const useCreateSaleDocument = () => {
    const globalStateDispatch = useGlobalStateDispatch();
    const { result, request, error } = usePromise(saleAPI.useCreateSaleDocument, {

        onSuccess: ({ data = false }) => {
            setBasket(globalStateDispatch, data);
        },
        // onFailure:()=>{ }
    })
    return {
        resultCreateSaleDocument: result,
        createSaleDocument: request,
        errorCreateSaleDocument: error || null
    }
};

export const useGetEcommerceBasketFromCustomerIdOrDocumentCode = () => {
    const globalStateDispatch = useGlobalStateDispatch();
    const { request } = usePromise(saleAPI.useGetEcommerceBasketFromCustomerIdOrDocumentCode, {
        onSuccess: ({ data = null }) => {
            setBasket(globalStateDispatch, data);
        },
        // onFailure:()=>{ }
    })
    return {
        getEcommerceBasket: request,
    }
};

/**
 * A custom hook to update customer information in the global state.
 *
 * @function useUpdateCustomer
 * @returns {Object} An object containing the `updateCustomer` function.
 * @returns {Function} return.updateCustomer - A function to trigger the customer update request. 
 */
export const useUpdateCustomer = () => {
    // Dispatch function to update global state
    const globalStateDispatch = useGlobalStateDispatch();
    const loadingContextAndErrorContext = useContext(ErrorAndLoadingContext);
    const { language } = useGlobalState();
    const { request } = usePromise(saleAPI.useUpdateCustomer, {
        onSuccess: ({ data = false }) => {
            setBasket(globalStateDispatch, data);
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_SUCCESS'), 'success');
        },
        onFailure: (error) => {

        }
    });

    return {
        updateCustomer: request
    };
};



/**
 * Custom hook to check if a cart exists for a customer by their ID.
 *
 * This hook provides a function to initiate the request and 
 * returns the result indicating whether the cart exists.
 *
 * @returns {Object} - An object containing the following properties:
 * @returns {boolean|null} cartIsExist - Indicates if the cart exists (true or false), or null if not checked yet.
 * @returns {Function} isCartExist - Function to initiate the request to check if a cart exists.
 */
export const useCheckIfCartExistsByCustomerId = () => {

    const { licenceData: { documentCode, user: { licences } } } = useGlobalState();
    const { updateCustomer } = useUpdateCustomer();
    const { codeShop } = useSettingsApps();
    let copyEcommerceCustomerId = (licences || []).find(({ licenceCode }) => (licenceCode === codeShop))?.ecommerceCustomerId;

    const { getEcommerceBasket } = useGetEcommerceBasketFromCustomerIdOrDocumentCode();

    const hundlerGetEcommerceBasket = (isBasket, documentCode) => {

        if (isBasket) {
            getEcommerceBasket();
        } else if (documentCode && copyEcommerceCustomerId) {
            updateCustomer({ documentCode, customerId: copyEcommerceCustomerId });
        } else if (documentCode) {
            getEcommerceBasket(documentCode);

        }

    }

    const { result, request } = usePromise(saleAPI.useCheckIfCartExistsByCustomerId, {
        onSuccess: (res) => {
            hundlerGetEcommerceBasket(res.data, documentCode);
        },
        onFailure: () => {
            hundlerGetEcommerceBasket(false, documentCode);
        }
    })
    return {
        cartIsExist: result?.data,
        isCartExist: () => {
            if (copyEcommerceCustomerId)
                request(copyEcommerceCustomerId);
            else
                hundlerGetEcommerceBasket(false, documentCode);
        }
    }
};

/**
 * Custom hook to handle abandoning a sale document.
 * 
 * This hook provides a method (`abandonSaleDocument`) that triggers an API request to abandon
 * the current sale document. On success, it displays a success message and clears the basket
 * in the global state. On failure, it shows an error message.
 *
 * @returns {Object} An object with the `abandonSaleDocument` method to trigger the abandon sale document request.
 */
export const useAbandonSaleDocument = () => {
    const loadingContextAndErrorContext = useContext(ErrorAndLoadingContext);
    const globalStateDispatch = useGlobalStateDispatch();
    const { resultCancelYavinPayment,
        setResultCancelYavinPayment,
        cancelYavinPayment } = useCancelYavinPayment();
    const { language, licenceData: {
        saleDocument: {
            paymentProcess
        } } } = useGlobalState();

    const { request } = usePromise(saleAPI.useAbandonSaleDocument, {
        onSuccess: () => {
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_SUCCESS'), 'success');
            // TODO: Remove the document code from the browser cache if necessary.
            setBasket(globalStateDispatch, null);
            setOldBasket(globalStateDispatch,null);
        },
        onFailure: () => {
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_FAILED'), 'error');
        }
    });
    useEffect(() => {
        if (resultCancelYavinPayment === 'OPERATION_SUCCESS')
            request();
        else if (resultCancelYavinPayment === 'OPERATION_FAILURE')
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_FAILED'), 'error');
        setResultCancelYavinPayment(null);
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , [resultCancelYavinPayment]);

    return {
        abandonSaleDocument: (body) => {
            if (paymentProcess)
                cancelYavinPayment(body);
            else
                request(body);
        }
    };
};



/**
 * A custom hook for updating the quantity of a line item in a global state.
 *
 * @function
 * @returns {Object} An object containing the updateLineQuantity function.
 * @returns {Function} return.updateLineQuantity - A function to update the quantity of a line item.
 *
 * @example
 * const { updateLineQuantity } = useUpdateLineQuantity();
 * updateLineQuantity({ itemId: 1, quantity: 3 });
 */
export const useUpdateLineQuantity = (callback = () => null) => {
    // Dispatch function to update global state
    const globalStateDispatch = useGlobalStateDispatch();
    const loadingContextAndErrorContext = useContext(ErrorAndLoadingContext);
    //Hook to delete a line from the sale
    const { deleteLine } = useDeleteLine();
    const { deleteLineParams } = useHandlerParamsApi();
    // Destructure necessary values from global state  
    const { language, templateData: { deliveryFees, freeDeliveryThreshold }, } = useGlobalState();
    const { request, result, error } = usePromise(saleAPI.updateLineQuantity, {
        onSuccess: ({ data = false }) => {
            setBasket(globalStateDispatch, data);
            callback();
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_SUCCESS'), 'success');
        },
        // onFailure: () => { }
    });
    useEffect(() => {      // Check if the line with type "DELIVERY_FEES_AUTO_ADDED" exists and is not deleted or canceled
        // If it exists, delete it
        let freeDeliveryLine = (result?.data?.lines || [])?.find(item => item?.lineInputType === "DELIVERY_FEES_AUTO_ADDED" && !(item?.lineDeleted || item?.lineCanceledReversal || item?.lineCancellationReversal));

        if (freeDeliveryLine && ((TransformToNumber(result?.data?.totalGlobalInclVatNetNetDc) - TransformToNumber(deliveryFees)) >= TransformToNumber(freeDeliveryThreshold) &&
            TransformToNumber(freeDeliveryThreshold) > 0)) {
            if (freeDeliveryLine?.lineIndex >= 0)
                deleteLine({ ...deleteLineParams(freeDeliveryLine?.lineIndex) })

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result?.data]);

    return {
        updateLineQuantity: request,
        errorUpdateLineQuantity: error || null
    };
};


/**
* Custom hook to handle adding an article by its ID to the current sale document.
* It utilizes global state, a sale API, and manages sale document creation if needed.
*
* @returns {Object} - An object containing the `addArticleById` function.
* 
* @function addArticleById
* @param {Object} body - The request body for adding an article, containing article-specific data.
* @example
* const { addArticleById } = useAddArticleById();
* addArticleById({ articleId: 12345 });
*/
var copyPropsAddArticleById = null;
var link = null;
export const useAddArticleById = (callback = () => null) => {
    const [errorAddArticle, setError] = useState();
    const loadingContextAndErrorContext = useContext(ErrorAndLoadingContext);
    const { language } = useGlobalState();
    const { updateLineQuantity, errorUpdateLineQuantity } = useUpdateLineQuantity(() => callback(link))
    // Destructure necessary values from global state
    const { templateData: { deliveryFees, freeDeliveryThreshold }, licenceData: { documentCode, saleDocument, user: { licences } } } = useGlobalState();

    // Hook to create a sale document
    const { createSaleDocument, resultCreateSaleDocument, errorCreateSaleDocument } = useCreateSaleDocument();
    //Hook to delete a line from the sale
    const { deleteLine } = useDeleteLine();
    const { deleteLineParams } = useHandlerParamsApi();

    // Retrieve the shop's code
    const { codeShop } = useSettingsApps();

    // Find the customer ID associated with the license code
    let customerId = (licences || []).find(({ licenceCode }) => (licenceCode === codeShop))?.ecommerceCustomerId;

    // Dispatch function to update global state
    const globalStateDispatch = useGlobalStateDispatch();

    // Handle adding the article using the sale API
    const { request, result, error } = usePromise(saleAPI.useAddArticleById, {
        /**
         * Success handler when the article is added.
         * Updates the basket and resets `copyPropsAddArticleById`.
         * @param {Object} response - The API response.
         * @param {Object} response.data - The updated sale document or basket data.
         */
        onSuccess: ({ data = null }) => {
            callback(link);
            // setTimeout(()=>{ 
            setBasket(globalStateDispatch, data);
            //},300)

            copyPropsAddArticleById = null;
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'PRODUCT_ADDED_SUCCESSFULLY'), 'success');


        },

        /**
         * Failure handler when the request fails.
         * Resets `copyPropsAddArticleById`.
         */
        onFailure: (error) => {
            copyPropsAddArticleById = null;
            link = null;

            //    if(['DELIVERY_COUNTRY_NOT_AVAILABLE','CUSTOMER_DELIVERY_COUNTRY_NOT_IN_AVAILABLE_LIST'].includes(code))
            //    loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_FAILED'), 'error');
        }
    });

    // Effect that triggers when `documentCode` changes
    useEffect(() => {
        if (documentCode && resultCreateSaleDocument?.status === 200)
            request({ ...copyPropsAddArticleById, key: documentCode });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultCreateSaleDocument]);

    useEffect(() => {      // Check if the line with type "DELIVERY_FEES_AUTO_ADDED" exists and is not deleted or canceled
        // If it exists, delete it
        let freeDeliveryLine = (result?.data?.lines || [])?.find(item => item?.lineInputType === "DELIVERY_FEES_AUTO_ADDED" && !(item?.lineDeleted || item?.lineCanceledReversal || item?.lineCancellationReversal));

        if (freeDeliveryLine && ((TransformToNumber(result?.data?.totalGlobalInclVatNetNetDc) - TransformToNumber(deliveryFees)) >= TransformToNumber(freeDeliveryThreshold) &&
            TransformToNumber(freeDeliveryThreshold) > 0)) {
            if (freeDeliveryLine?.lineIndex >= 0)
                deleteLine({ ...deleteLineParams(freeDeliveryLine?.lineIndex) })

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result?.data]);

    useEffect(() => {
        if (error?.response?.data?.code || errorCreateSaleDocument?.response?.data?.code || errorUpdateLineQuantity?.response?.data?.code)
            setError(error?.response?.data?.code || errorCreateSaleDocument?.response?.data?.code || errorUpdateLineQuantity?.response?.data?.code)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, errorCreateSaleDocument, errorUpdateLineQuantity]);


    return {
        /**
         * Adds an article by its ID to the sale document.
         * If the sale document doesn't exist, it will create one first.
         * 
         * @param {Object} body - The request body for the article.
         * @param {number} body.articleId - The ID of the article to add.
         * @param {number} body.quantity - The quantity of the article to add.
         */
        addArticleById: (body) => {
            link = body?.link;
            if (!!documentCode && !!saleDocument) {
                let article = (body?.lines || [])?.filter(
                    item => (!item?.lineDeleted && !item?.lineCanceledReversal && !item?.lineCancellationReversal))?.find(item => item?.articleId === body?.articleId);


                if (!!article)
                    if (TransformToNumber(article?.quantityConditioning) + TransformToNumber(body?.quantity) > body?.availableStock && !body?.withNegativeStock)
                        loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'ERROR_AVAILABLE_STOCK'), 'error');
                    else
                        updateLineQuantity({
                            ...body,
                            lineIndex: article?.lineIndex,
                            quantity: (TransformToNumber(article?.quantityConditioning) + TransformToNumber(body?.quantity))
                        });
                else
                    request(body);

            } else {
                copyPropsAddArticleById = body;
                createSaleDocument(customerId);
            }
        },
        setError,
        errorAddArticle
    };
};



/**
 * Custom hook to delete a line from the sale and update the global basket state.
 *
 * This hook leverages `usePromise` to handle API requests for deleting a sale line,
 * and updates the global state upon a successful request.
 *
 * @returns {Object} An object containing the deleteLine function, which is used to
 * initiate the delete request.
 *
 * @property {Function} deleteLine - The function to trigger the delete line request.
 * This function returns a promise and updates the basket in the global state if the
 * request is successful. 
 */
export const useDeleteLine = () => {
    // Dispatch function to update global state
    const globalStateDispatch = useGlobalStateDispatch();

    // API request for deleting a sale line, with a success callback
    const { result,request } = usePromise(saleAPI.useDeleteLine, {
        onSuccess: ({ data = null }) => {
            setBasket(globalStateDispatch, data);
        },
    });

    return {
        deleteLine: request,
        resultDeleteLine:result?.data
    };
};

/**
 * A custom hook for validating a sale document and updating the global state.
 *
 * @function
 * @returns {Object} An object containing the validateBasket function.
 * @returns {Function} return.validateBasket - A function to validate the sale document. 
 */ 

export const useValidateSaleDocument = () => {
    const loadingContextAndErrorContext = useContext(ErrorAndLoadingContext);
    const { language,licenceData:{saleDocument} } = useGlobalState();
    const { validateSaleDocumentParams } = useHandlerParamsApi();

    const globalStateDispatch = useGlobalStateDispatch();

    const [linkingPayment, setLinkingPayment] = useState(false);

    // Always reflects the latest saleDocument to avoid stale closure in async callbacks
    const saleDocumentRef = useRef(saleDocument);
    saleDocumentRef.current = saleDocument;

    // Store payment data for retry without triggering re-renders
    const yavinPaymentRef = useRef(null);

    //  Retry control to avoid infinite loops
    const retryCountRef = useRef(0);
    const MAX_RETRY = 2;

    /* ---------------- VALIDATE SALE DOCUMENT ---------------- */
    const { request: validateRequest, loading } = usePromise(
        saleAPI.useValidateSaleDocument,
        {
            onSuccess: (data) => {
                // Clear basket after successful validation
                setBasket(globalStateDispatch, null);
            setOldBasket(globalStateDispatch,data?.data);

                // Show success message
                loadingContextAndErrorContext.snackbar(
                    true,
                    getTranslation(language, "OPERATION_SUCCESS"),
                    "success"
                );
            },
        }
    );

    /* ---------------- CREATE YAVIN PAYMENT ---------------- */
    const { request: requestCreateYavinPayment } = usePromise(
        saleAPI.useCreateYavinPayment,
        {
            onSuccess: (data) => {
                // Reset retry state on success
                retryCountRef.current = 0;
                yavinPaymentRef.current = null;
                setBasket(globalStateDispatch, {...saleDocumentRef.current,paymentProcess:true});
                // Store payment link
                setLinkingPayment(()=>data?.data);
            },

            onFailure: (error) => {
                const shouldRetry =
                    error?.data?.code === "ERROR_GENERATE_LINK" &&
                    yavinPaymentRef.current &&
                    retryCountRef.current < MAX_RETRY;

                if (shouldRetry) {
                    // Increment retry count and cancel previous payment
                    retryCountRef.current += 1;
                    cancelYavinPayment(yavinPaymentRef.current);
                } else {
                    // Reset everything if retry is not allowed
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_FAILED', errorTranslation), 'error');
                    retryCountRef.current = 0;
                    yavinPaymentRef.current = null;
                }
            },
        }
    );

    /* ---------------- CANCEL YAVIN PAYMENT ---------------- */
    const { request: cancelYavinPayment } = usePromise(
        saleAPI.useCancelYavinPayment,
        {
            onSuccess: () => {
                // Retry creating payment after successful cancellation
                if (yavinPaymentRef.current) {
                    requestCreateYavinPayment(yavinPaymentRef.current);
                }
            },

            onFailure: () => {
                // Reset retry state if cancel fails
                retryCountRef.current = 0;
                yavinPaymentRef.current = null; 
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_FAILED', errorTranslation), 'error');
        
            },
        }
    );

    /* ---------------- PUBLIC FUNCTION ---------------- */
    const createYavinPayment = (body) => {
        const params = validateSaleDocumentParams(body);

        // Reset retry state and UI
        retryCountRef.current = 0;
        setLinkingPayment(()=>false);

        // Store params for potential retry
        yavinPaymentRef.current = params;

        // Trigger API call
        requestCreateYavinPayment(params);
    };

    return {
        // Validate basket / sale document
        validateBasket: (body) =>
            validateRequest(validateSaleDocumentParams(body)),

        linkingPayment,
        setLinkingPayment,

        // Create payment with retry mechanism
        createYavinPayment,

        loadingCreateYavinPayment: loading,
    };
};
export const useAddSalePaymentDocument = () => {
    // Dispatch function to update global state
    const globalStateDispatch = useGlobalStateDispatch();
    const { addSalePaymentParams } = useHandlerParamsApi();

    const { request, result, error, loading } = usePromise(saleAPI.useAddSalePayment, {
        onSuccess: (data) => {
            setBasket(globalStateDispatch, null);
            setOldBasket(globalStateDispatch,data?.data);
            // loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_SUCCESS'), 'success');
        }, onFailure: () => {
            setBasket(globalStateDispatch, null);

        }
    });
    return {
        resultAddSalePaymentDocument: result || error,
        addSalePaymentDocument: (body) => request(addSalePaymentParams(body)),
        loadingAddSalePaymentDocument: loading
    };
};


export const useUpdateHeaderFieldNotValidatedDocumentApi = (body) => {
    const globalStateDispatch = useGlobalStateDispatch();

    const { updateHeaderFieldParams } = useHandlerParamsApi();

    const { request, loading } = usePromise(saleAPI.useUpdateHeaderFieldNotValidatedDocumentApi, {
        onSuccess: ({ data }) => {
            setBasket(globalStateDispatch, data);
        },
    });
    return {
        updateHeaderField: (body) => request(updateHeaderFieldParams(body)),
        loadingUpdateHeaderField: loading
    };
};

export const useCancelYavinPayment = () => {
    const loadingContextAndErrorContext = useContext(ErrorAndLoadingContext);
    const { language } = useGlobalState();
    const { validateSaleDocumentParams } = useHandlerParamsApi();
    const [resultCancelYavinPayment, setResultCancelYavinPayment] = useState();
    // Dispatch function to update global state 
    const { request, loading } = usePromise(saleAPI.useCancelYavinPayment, {
        onSuccess: () => {
            setResultCancelYavinPayment('OPERATION_SUCCESS');
            loadingContextAndErrorContext.snackbar(true, getTranslation(language, 'OPERATION_SUCCESS'), 'success');
        },
        onFailure: (error) => {
            setResultCancelYavinPayment(error?.code || 'OPERATION_FAILURE');
        }
    });
    return {
        resultCancelYavinPayment, setResultCancelYavinPayment,
        cancelYavinPayment: (body) => request(validateSaleDocumentParams(body)),
        loadingCancelYavinPayment: loading
    }
};
/**
 * A custom hook to retrieve the order history for a customer. 
 * @property {Array} orderHistoryList - The list of past orders for the customer.
 * @property {Function} getOrderHistory - A function to fetch the order history from the API.
 *
 * @example
 * const { orderHistoryList, getOrderHistory } = useGetOrderHistory();
 * useEffect(() => {
 *   getOrderHistory(customerId);
 * }, [customerId]);    
 */
export const useGetOrderHistory = () => {
    const [orderHistoryList, setOrderHistoryList] = useState([]);
    const [loadingOrderHistoryList,setLoadingOrderHistoryList] = useState(false)
    const { request} = usePromise(
     saleAPI.useGetOrderHistory, 
      {
        onSuccess: data => { 
          setOrderHistoryList(data?.data?.listData || []);
          setLoadingOrderHistoryList(false);
        },
        onFailure: () => {
           setOrderHistoryList([]);
             setLoadingOrderHistoryList(false);
        }
      },
    );
  
    return {
     orderHistoryList,
     loadingOrderHistoryList,
     getOrderHistory:(body)=>{
        setLoadingOrderHistoryList(true);
        request(body);}
    };
  }
export const useUpdateNotValidatedDocumentApi = () => {
    const globalStateDispatch = useGlobalStateDispatch();

    const { request, loading } = usePromise(saleAPI.useUpdateSaleDocumentNotValidated, {
        onSuccess: ({ data }) => { 
            setBasket(globalStateDispatch, data);
        },
    });
    return {
        updateNotValidDocument: request,
        loadingUpdateNotValidDocument: loading
    };
};

  